/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Reusable form open->submit timing hook.
 *
 * Captures `t0` on mount (the form/wizard open) and exposes `markSubmitted`,
 * which the owning form calls on a successful submit to emit a `form_timing`
 * telemetry event carrying `feature`/`operation`/`form_open_to_submit_ms`.
 * Authenticated-only emission is enforced inside the telemetry emitter, so this
 * hook is safe to mount everywhere.
 */
import { useCallback, useRef } from 'react';
import { emitTelemetry } from '@services/telemetry/telemetry';

export interface FormTimingHandle {
  /**
   * Emit a `form_timing` event for a successful submit. Idempotent per mount:
   * only the first call after mount reports, so a double-submit does not double
   * count. Call again only after a fresh `reset()`.
   */
  markSubmitted: (args: { feature: string; operation?: string }) => void;
  /** Reset the open timestamp (e.g. when a wizard is reused for a new entity). */
  reset: () => void;
}

/**
 * Track form-open to submit-success timing.
 *
 * @returns A stable handle with `markSubmitted` and `reset`.
 */
export function useFormTiming(): FormTimingHandle {
  const openedAtRef = useRef<number>(Date.now());
  const reportedRef = useRef<boolean>(false);

  const markSubmitted = useCallback(
    ({ feature, operation }: { feature: string; operation?: string }) => {
      if (reportedRef.current) return;
      reportedRef.current = true;
      const elapsed = Math.max(0, Math.round(Date.now() - openedAtRef.current));
      emitTelemetry({
        event_type: 'form_timing',
        feature,
        operation,
        form_open_to_submit_ms: elapsed,
      });
    },
    [],
  );

  const reset = useCallback(() => {
    openedAtRef.current = Date.now();
    reportedRef.current = false;
  }, []);

  return { markSubmitted, reset };
}
